import axios from 'axios';
import qs from 'query-string';
import { createAsyncThunk, uploadImageHandler } from 'app/store/utils';
import {
  CreatorIdentifiersResponse,
  FetchSocietiesResponse,
  Link,
} from 'types/api.types';
import {
  AddIpnData,
  AddIpnManuallyResponse,
  FetchIpnValidationsResponse,
  UpdateIpnData,
  UpdateIpnResponse,
} from 'types/ipn.types';
import {
  AddIpiData,
  AddIpiManuallyResponse,
  FetchIpiValidationsResponse,
  UpdateIpiData,
  UpdateIpiResponse,
} from 'types/ipi.types';
import { apiErrorHandler } from 'app/utils/utils';
import {
  ProfileFormData,
  RepresentativeFormData,
} from 'app/components/Profile/ProfileForm/types';
import {
  convertProfileFormDataForBE,
  convertRepresentativeFormDataForBE,
} from 'app/views/Profile/utils';
import { DDL, Option, UploadImageRequestData } from 'types/general.types';

import {
  getAddIpiV0LinkSelector,
  getAddUnverifiedIpnLink,
  getCmoSocietiesListLink,
  getConnectionsListLink,
  getCreateIsniLink,
  getCreateProfileImageLinkSelector,
  getCurrentProfile,
  getDeleteProfileImageLinkSelector,
  getMyLegalProfileLink,
  getPreferredIpi,
  getPreferredIpn,
  getPreferredIsni,
  getProfileIdsLink,
  getProfileImageStatusLink,
  getProfileSearchLink,
  getProSocietiesListLink,
  getRepresentativeRolesLink,
  getUpdateIsniLink,
} from 'app/store/Profile/selectors';
import {
  ApiErrorCodeMessages,
  ApiErrorCodeTypes,
  ImageStatusType,
} from 'types/enums';
import { CreateIsniData, IsniResponse, UpdateIsniData } from 'types/isni.types';
import {
  AccountConnectionProviderId,
  AllPrivacySettings,
  FetchConnectionsResponse,
  FetchProfileResponse,
  FetchUploadImageStatusResponse,
  ProfileImage,
  SearchProfilesValidationSchemaResponse,
} from 'types/profile';
import { apiUrls } from 'app/api.config';
import { addAlertAction } from '../Alerts/slice';
import { getIsniSuccessMessage } from './effects.utils';

export const fetchProfileIdsEffect = createAsyncThunk(
  'profile/fetchProfileIds',
  async (_, { getState }) => {
    const link = getProfileIdsLink(getState());
    if (!link) {
      throw new Error('No ProfileIds link found');
    }
    const response = await axios.get<CreatorIdentifiersResponse>(link, {
      withCredentials: true,
    });
    const { ipn, ipi, isni } = response.data.response;
    return { ipn, ipi, isni };
  }
);

export const fetchLegalProfileEffect = createAsyncThunk(
  'profile/fetchLegalProfile',
  async (_, { getState }) => {
    const link = getMyLegalProfileLink(getState());
    if (!link) {
      throw new Error('Legal profile link not found');
    }
    const response = await axios.get<FetchProfileResponse>(link.href, {
      withCredentials: true,
    });
    return response.data.response.profile.item;
  }
);

export const updateProfileEffect = createAsyncThunk(
  'profile/updateProfile',
  async (
    data: {
      formData: Partial<ProfileFormData>;
      options?: { hideAlerts: boolean };
    },
    { getState, dispatch }
  ) => {
    const state = getState();
    const currentProfile = getCurrentProfile(state);
    const link = currentProfile?._links?.update.href;
    try {
      if (!link) {
        throw new Error('Update profile link not found');
      }
      const convertedData = convertProfileFormDataForBE(data.formData);
      const response = await axios.patch<FetchProfileResponse>(
        link,
        convertedData,
        {
          withCredentials: true,
        }
      );
      if (!data.options?.hideAlerts) {
        dispatch(
          addAlertAction({
            message: 'Your details have been updated successfully',
          })
        );
      }
      return response.data.response.profile.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Failed to update personal details' })
      );
      throw e;
    }
  }
);

export const fetchProSocietiesEffect = createAsyncThunk(
  'profile/fetchProSocieties',
  async (_, { getState }) => {
    const link = getProSocietiesListLink(getState())?.href;
    if (!link) {
      throw new Error('Pro Societies link not found');
    }
    const response = await axios.get<FetchSocietiesResponse>(link, {
      withCredentials: true,
    });
    const { items, _links } = response.data.response.societies;
    return { items, _links };
  }
);

export const fetchCmoSocietiesEffect = createAsyncThunk(
  'profile/fetchCmoSocieties',
  async (_, { getState }) => {
    const link = getCmoSocietiesListLink(getState())?.href;
    if (!link) {
      throw new Error('Cmo Societies list link not found');
    }
    const response = await axios.get<FetchSocietiesResponse>(link, {
      withCredentials: true,
    });
    const { items, _links } = response.data.response.societies;
    return { items, _links };
  }
);

export const fetchIpiValidationsEffect = createAsyncThunk(
  'profile/fetchIpiValidations',
  async (_, { getState }) => {
    const link = getAddIpiV0LinkSelector(getState())?.href;
    if (!link) {
      throw new Error('Add IPI link not found');
    }
    const response = await axios.request<FetchIpiValidationsResponse>({
      url: link,
      method: 'POST',
      headers: { Accept: 'application/json+schema' },
      withCredentials: true,
    });

    return response.data.response.schema.properties.body;
  }
);

export const fetchIpnValidationsEffect = createAsyncThunk(
  'profile/fetchIpnValidations',
  async (_, { getState }) => {
    const link = getAddUnverifiedIpnLink(getState())?.href;
    if (!link) {
      throw new Error('Add IPN link not found');
    }
    const response = await axios.request<FetchIpnValidationsResponse>({
      url: link,
      method: 'POST',
      headers: { Accept: 'application/json+schema' },
      withCredentials: true,
    });

    return response.data.response.schema.properties.body;
  }
);

export const addIpiManuallyEffect = createAsyncThunk(
  'profile/addIpiManually',
  async (
    data: { formData: AddIpiData; societyTitle: string },
    { getState, dispatch }
  ) => {
    const state = getState();
    const link = getAddIpiV0LinkSelector(state)?.href;
    const preferredIpi = getPreferredIpi(state);
    try {
      if (!link) {
        throw new Error('Add IPI link not found');
      }
      const response = await axios.post<AddIpiManuallyResponse>(
        link,
        data.formData,
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        dispatch(addAlertAction({ message: 'IPI has already been added' }));
      } else if (response.status === 201) {
        dispatch(
          addAlertAction({
            message: preferredIpi
              ? 'IPI Number has been added'
              : 'IPI successfully added, you can now set a default',
          })
        );
      }

      return { item: response.data.response.ipi.item, status: response.status };
    } catch (e: any) {
      dispatch(addAlertAction({ message: apiErrorHandler(e?.response) }));
      throw e;
    }
  }
);

export const addIpnManuallyEffect = createAsyncThunk(
  'profile/addIpnManually',
  async (
    data: { formData: AddIpnData; societyTitle: string },
    { getState, dispatch }
  ) => {
    const state = getState();
    const link = getAddUnverifiedIpnLink(state)?.href;
    const preferredIpn = getPreferredIpn(state);
    try {
      if (!link) {
        throw new Error('Add IPN link not found');
      }
      const response = await axios.post<AddIpnManuallyResponse>(
        link,
        data.formData,
        {
          withCredentials: true,
        }
      );

      if (response.status === 200) {
        dispatch(addAlertAction({ message: 'IPN has already been added' }));
      } else if (response.status === 201) {
        dispatch(
          addAlertAction({
            message: preferredIpn
              ? 'IPN Number has been added'
              : 'IPN successfully added, you can now set a default',
          })
        );
      }

      return { item: response.data.response.ipn.item, status: response.status };
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to add IPN number. Please try again later.',
        })
      );
      throw e;
    }
  }
);

export const createIsniEffect = createAsyncThunk(
  'profile/createIsniEffect',
  async (data: CreateIsniData, { getState, dispatch }) => {
    const state = getState();
    const link = getCreateIsniLink(state);
    const preferredIsni = getPreferredIsni(state);

    try {
      if (!link) {
        throw new Error('Add ISNI link not found');
      }
      const response = await axios.request<IsniResponse>({
        url: link.href,
        method: link.method,
        data,
        withCredentials: true,
      });
      const successMessage = getIsniSuccessMessage(
        response.status,
        Boolean(preferredIsni)
      );
      if (successMessage) {
        dispatch(addAlertAction({ message: successMessage }));
      }
      return {
        item: response.data.response.isni.item,
        status: response.status,
      };
    } catch (e: any) {
      if (
        e.response?.data?.error?.errorCode === ApiErrorCodeTypes.INVALID_ISNI
      ) {
        dispatch(
          addAlertAction({
            message: ApiErrorCodeMessages.INVALID_ISNI,
          })
        );
      } else {
        dispatch(
          addAlertAction({
            message: 'Failed to add ISNI number. Please try again later.',
          })
        );
      }
      throw e;
    }
  }
);

export const updateIsniEffect = createAsyncThunk(
  'profile/updateIsniEffect',
  async (
    { data, isniNumber }: { data: UpdateIsniData; isniNumber?: string },
    { getState, dispatch }
  ) => {
    const state = getState();
    const link = getUpdateIsniLink(isniNumber)(state);
    try {
      if (!link) {
        throw new Error('Update ISNI link not found');
      }
      const response = await axios.request<IsniResponse>({
        url: link.href,
        method: link.method,
        data,
        withCredentials: true,
      });
      dispatch(
        addAlertAction({
          message: 'ID has been updated',
        })
      );
      return {
        item: response.data.response.isni.item,
        status: response.status,
      };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const updateIpnEffect = createAsyncThunk(
  'profile/updateIpn',
  async (
    data: { formData: Partial<UpdateIpnData>; link?: Link },
    { dispatch }
  ) => {
    try {
      if (!data.link) {
        throw new Error('Update IPN link not found');
      }
      const response = await axios.request<UpdateIpnResponse>({
        url: data.link.href,
        method: data.link.method,
        data: data.formData,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'ID has been updated' }));
      return response.data.response.ipn.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const updateIpiEffect = createAsyncThunk(
  'profile/updateIpi',
  async (
    data: { formData: Partial<UpdateIpiData>; link?: Link },
    { dispatch }
  ) => {
    try {
      if (!data.link) {
        throw new Error('Edit IPI link not found');
      }

      const { isDefault, ipiNameNumber, ...formData } = data.formData;
      const response = await axios.request<UpdateIpiResponse>({
        url: data.link.href,
        method: data.link.method,
        data: formData,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'ID has been updated' }));
      return response.data.response.ipi.item;
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const uploadProfileImageEffect = createAsyncThunk(
  'profile/uploadProfileImage',
  async (data: UploadImageRequestData, { dispatch, getState }) => {
    const link = getCreateProfileImageLinkSelector(getState())?.href;
    try {
      if (!link) {
        throw new Error('Upload profile image link not found');
      }
      const { uploadStatusLink } = await uploadImageHandler(link, data);
      return uploadStatusLink;
    } catch (e) {
      if (!axios.isCancel(e)) {
        dispatch(
          addAlertAction({ message: 'Unexpected error, please try again' })
        );
      }
      throw e;
    }
  }
);

export const fetchProfileImageStatusEffect = createAsyncThunk(
  'profile/fetchProfileImageStatus',
  async (_, { dispatch, getState }) => {
    const link = getProfileImageStatusLink(getState())?.href;
    let images: ProfileImage[] | null = null;
    try {
      if (!link) {
        throw new Error('Profile image status link not found');
      }
      const response = await axios.get<FetchUploadImageStatusResponse>(link, {
        withCredentials: true,
      });
      const { status } = response.data.response.image.item;
      if (status === ImageStatusType.SUCCESS) {
        const legalProfileLink = getMyLegalProfileLink(getState());

        if (!legalProfileLink) {
          throw new Error('Legal profile link not found');
        }
        const profileResponse = await axios.get<FetchProfileResponse>(
          legalProfileLink.href,
          {
            withCredentials: true,
          }
        );
        const { profileImages } = profileResponse.data.response.profile.item;
        if (profileImages) {
          images = profileImages;
        }
      }
      return { status, images };
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const deleteIpiEffect = createAsyncThunk(
  'profile/deleteIpi',
  async (
    data: { ipiNameNumber: number; link?: Link },
    { dispatch, getState }
  ) => {
    const state = getState();
    const { ipiNameNumber, link } = data;
    const preferredIpi = getPreferredIpi(state);
    try {
      if (!link) {
        throw new Error('Delete IPI link not found');
      }
      await axios.request({
        url: link?.href,
        method: link?.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'IPI Number has been deleted' }));
      if (preferredIpi?.number === ipiNameNumber) {
        await dispatch(
          updateProfileEffect({
            formData: {
              preferredIpi: {
                value: (null as unknown) as Option['value'],
                title: '',
              },
            },
            options: { hideAlerts: true },
          })
        );
      }
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to delete IPI Number, please try again',
        })
      );
      throw e;
    }
  }
);

export const deleteIpnEffect = createAsyncThunk(
  'profile/deleteIpn',
  async (data: { ipnNumber: number; link?: Link }, { dispatch, getState }) => {
    const state = getState();
    const { ipnNumber, link } = data;
    const preferredIpn = getPreferredIpn(state);
    try {
      if (!link) {
        throw new Error('Delete IPN link not found');
      }
      await axios.request({
        url: link?.href,
        method: link?.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'IPN Number has been deleted' }));
      if (preferredIpn?.number === ipnNumber) {
        await dispatch(
          updateProfileEffect({
            formData: {
              preferredIpn: {
                value: (null as unknown) as Option['value'],
                title: '',
              },
            },
            options: { hideAlerts: true },
          })
        );
      }
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to delete IPN Number, please try again',
        })
      );
      throw e;
    }
  }
);

export const deleteIsniEffect = createAsyncThunk(
  'profile/deleteIsni',
  async (data: { isniNumber: string; link?: Link }, { dispatch, getState }) => {
    const state = getState();
    const { link, isniNumber } = data;
    const preferredIsni = getPreferredIsni(state);

    try {
      if (!link) {
        throw new Error('Delete ISNI link not found');
      }
      await axios.request({
        url: link?.href,
        method: link?.method,
        withCredentials: true,
      });
      dispatch(addAlertAction({ message: 'ISNI Number has been deleted' }));
      if (preferredIsni?.number === isniNumber) {
        await dispatch(
          updateProfileEffect({
            formData: {
              preferredIsni: {
                value: (null as unknown) as Option['value'],
                title: '',
              },
            },
            options: { hideAlerts: true },
          })
        );
      }
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to delete ISNI Number, please try again',
        })
      );
      throw e;
    }
  }
);

export const fetchSearchProfilesValidationsEffect = createAsyncThunk(
  'songs/fetchSearchProfilesValidations',
  async (_, { getState }) => {
    const searchProfilesLink = getProfileSearchLink(getState());

    if (!searchProfilesLink) {
      throw new Error('Search Profiles Link Not Found');
    }

    const response = await axios.request<SearchProfilesValidationSchemaResponse>(
      {
        url: searchProfilesLink.href.replace(/{.*.}/, ''),
        method: searchProfilesLink.method,
        headers: { Accept: 'application/json+schema' },
        withCredentials: true,
      }
    );
    return response.data.response.schema.properties.query.properties;
  }
);

export const fetchConnectionsEffect = createAsyncThunk(
  'profile/fetchConnections',
  async (
    data: { providerId?: AccountConnectionProviderId } | undefined,
    { getState }
  ) => {
    const link = getConnectionsListLink(getState());
    if (!link) {
      throw new Error('No Profile Connection link found');
    }
    const { url, query } = qs.parseUrl(
      link.href.replace('{providerId}', data?.providerId || '')
    );
    const requestUrl = qs.stringifyUrl({
      url,
      query: query.providerId ? query : undefined,
    });
    const response = await axios.get<FetchConnectionsResponse>(requestUrl, {
      withCredentials: true,
    });

    return response.data.response.accountConnections.items;
  }
);

export const deleteConnectionEffect = createAsyncThunk(
  'profile/deleteConnection',
  async (
    data: {
      providerId: string;
      deleteLink: Link;
      connectionGuid: string;
      name: string;
    },
    { dispatch }
  ) => {
    try {
      await axios.request({
        url: data.deleteLink.href,
        method: data.deleteLink.method,
        withCredentials: true,
      });
      dispatch(
        addAlertAction({
          message: `Your ${data.name} connection has been removed`,
        })
      );
    } catch (e) {
      dispatch(
        addAlertAction({
          message: 'Failed to remove connection, please try again',
        })
      );
      throw e;
    }
  }
);

export const deleteProfileImageEffect = createAsyncThunk(
  'profile/deleteProfileImage',
  async (_, { dispatch, getState }) => {
    const link = getDeleteProfileImageLinkSelector(getState());

    try {
      if (!link) {
        throw new Error('Profile delete image link not found');
      }
      await axios.request({
        url: link.href,
        method: link.method,
        withCredentials: true,
      });
    } catch (e) {
      dispatch(
        addAlertAction({ message: 'Unexpected error, please try again' })
      );
      throw e;
    }
  }
);

export const fetchProfileEffect = createAsyncThunk(
  'profile/fetchProfile',
  async ({ profileGuid }: { profileGuid: string }) => {
    const link = `${apiUrls.profiles}/${profileGuid}`;
    const response = await axios.get<FetchProfileResponse>(link, {
      withCredentials: true,
    });
    return {
      ...response.data.response.profile.item,
    };
  }
);

export const updatePrivacySettingsEffect = createAsyncThunk(
  'profile/updatePrivacySettings',
  async (privacySettings: AllPrivacySettings, { getState, dispatch }) => {
    const state = getState();
    const currentProfile = getCurrentProfile(state);
    const link = currentProfile?._links?.update.href;
    try {
      if (!link) {
        throw new Error('Update profile link not found');
      }

      const response = await axios.patch<FetchProfileResponse>(
        link,
        { ...privacySettings },
        {
          withCredentials: true,
        }
      );
      dispatch(
        addAlertAction({
          message: 'Privacy settings have been updated',
        })
      );
      return response.data.response.profile.item;
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to update representative' }));
      throw e;
    }
  }
);

export const fetchRepresentativeRolesEffect = createAsyncThunk(
  'profile/fetchRepresentativeRolesEffect',
  async (_, { getState }) => {
    const link = getRepresentativeRolesLink(getState());
    if (!link) {
      throw new Error('Representative roles link not found');
    }
    const { data } = await axios.get<{ roles: DDL }>(link.href);
    return data.roles;
  }
);

export const updateRepresentativeEffect = createAsyncThunk(
  'profile/updateRepresentative',
  async (
    { formData, index }: { formData: RepresentativeFormData; index: number },
    { getState, dispatch }
  ) => {
    const state = getState();
    const currentProfile = getCurrentProfile(state);
    const link = currentProfile?._links?.update.href;
    try {
      if (!link) {
        throw new Error('Update profile link not found');
      }

      const representative = convertRepresentativeFormDataForBE(formData);
      const representatives = currentProfile.representatives
        ? [...currentProfile.representatives]
        : [];

      representatives?.splice(index, 1, representative);
      const response = await axios.patch<FetchProfileResponse>(
        link,
        { representatives },
        {
          withCredentials: true,
        }
      );
      dispatch(
        addAlertAction({
          message: 'Representative have been updated',
        })
      );
      return response.data.response.profile.item;
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to update representative' }));
      throw e;
    }
  }
);

export const createRepresentativeEffect = createAsyncThunk(
  'profile/createRepresentative',
  async (formData: RepresentativeFormData, { getState, dispatch }) => {
    const state = getState();
    const currentProfile = getCurrentProfile(state);
    const link = currentProfile?._links?.update.href;
    try {
      if (!link) {
        throw new Error('Update profile link not found');
      }

      const representative = convertRepresentativeFormDataForBE(formData);
      const representatives = currentProfile.representatives
        ? [...currentProfile.representatives, representative]
        : [representative];

      const response = await axios.patch<FetchProfileResponse>(
        link,
        { representatives },
        {
          withCredentials: true,
        }
      );
      dispatch(
        addAlertAction({
          message: 'Representative have been created',
        })
      );
      return response.data.response.profile.item;
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to create representative' }));
      throw e;
    }
  }
);

export const deleteRepresentativeEffect = createAsyncThunk(
  'profile/deleteRepresentative',
  async (index: number, { getState, dispatch }) => {
    const state = getState();
    const currentProfile = getCurrentProfile(state);
    const link = currentProfile?._links?.update.href;
    try {
      if (!link) {
        throw new Error('Update profile link not found');
      }

      const representatives = currentProfile.representatives
        ? [...currentProfile.representatives]
        : [];
      representatives.splice(index, 1);

      const response = await axios.patch<FetchProfileResponse>(
        link,
        { representatives },
        {
          withCredentials: true,
        }
      );
      dispatch(
        addAlertAction({
          message: 'Representative have been deleted',
        })
      );
      return response.data.response.profile.item;
    } catch (e) {
      dispatch(addAlertAction({ message: 'Failed to delete representative' }));
      throw e;
    }
  }
);
